import { useRef, useState } from "react";
import { HorizontalBox } from "src/components/common/box/HorizontalBox";
import { AsyncButton } from "src/components/common/buttons/AsyncButton";
import i18n from "i18next";
import { PartConfiguratorType } from "src/api/generated/erp/configurator/configuratorType/partConfiguratorType.ts";
import { faBookmark, faClockRotateLeft } from "@fortawesome/pro-regular-svg-icons";
import { useGenericDialog } from "src/components/common/dialogs/GenericDialogContext.ts";
import {
	getConfiguratorTreeRecursiveInputErrors,
	getTreeItemRecursiveConfiguratorInput,
} from "src/components/views/erp/configurator/configuratorForm/configuratorTree/treeItemModel/configuratorTreeItemQueries.ts";
import { ConfiguratorTreeItemModel } from "src/api/generated/erp/configurator/configuratorTree/configuratorTreeItemModel.ts";
import { ConfiguratorTreeErrorsView } from "src/components/views/erp/configurator/configuratorForm/configuratorTree/ConfiguratorTreeErrorsView.tsx";
import { useConfigurationCheckpoints } from "src/components/views/erp/configurator/configuratorForm/checkpoints/ConfigurationCheckpointContext.ts";
import { ConfigurationCheckpointLogPopout } from "src/components/views/erp/configurator/configuratorForm/checkpoints/ConfigurationCheckpointLogPopout.tsx";
import { defineAgentUiContextKey } from "src/components/views/ai/assistant/uiContext/agentUiContextKey.ts";
import { getPartConfiguratorAgentContextData } from "src/components/views/erp/configurator/configuratorForm/utils/partConfiguratorTypeUtils.ts";

export interface ConfiguratorFormFooterPriceViewProps {
	rootConfiguratorItem: ConfiguratorTreeItemModel;
	currentInputVersion: number;
}

export const ConfiguratorFormFooterPriceView = ({
	rootConfiguratorItem,
	currentInputVersion,
}: ConfiguratorFormFooterPriceViewProps) => {
	const { openDialog } = useGenericDialog();
	const logAnchorRef = useRef<HTMLSpanElement>(null);
	const [logOpen, setLogOpen] = useState(false);

	const { entries: checkpointEntries, createCheckpoint, state: checkpointState } = useConfigurationCheckpoints();

	if (!configuratorCanBePriced(rootConfiguratorItem.configuratorType)) {
		return null;
	}

	const hasPendingChanges = checkpointState.lastCheckpointInputVersion !== currentInputVersion;

	return (
		<HorizontalBox sx={{ alignItems: "center", gap: 1 }}>
			<span ref={logAnchorRef}>
				<AsyncButton
					icon={faClockRotateLeft}
					size={"small"}
					tooltip={i18n.t("checkpoint_log")}
					onClick={() => setLogOpen((prev) => !prev)}
				/>
			</span>
			<AsyncButton
				icon={faBookmark}
				size={"small"}
				tooltip={i18n.t("save_checkpoint")}
				onClick={onSaveCheckpoint}
				disabled={!hasPendingChanges}
			/>
			<ConfigurationCheckpointLogPopout
				anchorEl={logAnchorRef.current}
				open={logOpen}
				onClose={() => setLogOpen(false)}
				entries={checkpointEntries}
				hasPendingChanges={hasPendingChanges}
			/>
		</HorizontalBox>
	);

	async function onSaveCheckpoint() {
		const inputErrors = getConfiguratorTreeRecursiveInputErrors(rootConfiguratorItem);
		if (inputErrors != null) {
			openDialog({
				title: i18n.t("configurator_form_errors_title"),
				size: "sm",
				content: <ConfiguratorTreeErrorsView errors={inputErrors} />,
				agentContext: {
					key: defineAgentUiContextKey("erp.configurator.configuratorCheckpointFormErrors.dialog"),
					title: i18n.t("configurator_form_errors_title"),
					data: getPartConfiguratorAgentContextData(rootConfiguratorItem.configuratorType),
				},
			});
			return;
		}

		const configuratorInput = getTreeItemRecursiveConfiguratorInput(rootConfiguratorItem);
		await createCheckpoint(configuratorInput, currentInputVersion);
	}
};

function configuratorCanBePriced(configuratorType: PartConfiguratorType): boolean {
	switch (configuratorType.type) {
		case "customerOrderLine":
		case "customerOrderOfferLine":
		case "unsavedCustomerOrderLine":
		case "unsavedCustomerOfferLine":
			return true;
		case "sitePartTest":
		case "sitePart":
		case "catalogPartTest":
		case "subConfigurator":
		case "shopOrderLine":
		case "shopOrder":
		case "unsavedShopOrder":
		case "purchaseOrderLine":
		case "projectActivityMaterial":
		case "configurationTemplateEditor":
		case "partConfigurationBomLine":
		case "salesPart":
		case "unsavedProjectActivityMaterial":
			return false;
	}
}
